import type { ListingRun, RunStatusSummary } from "@nova-ai/shared"
import { Archive, ArchiveRestore, Package, RefreshCw } from "lucide-react"
import { useId } from "react"

import { Tooltip } from "../ui/tooltip"
import { Button } from "../ui/button"
import { StatusBadge } from "../ui/status-badge"
import { cn } from "../../lib/utils"
import { formatDateTime } from "../../lib/format-date"
import { humanizeMarketplaceId } from "../../lib/marketplace"
import { MarketplaceLogo } from "../marketplaces/marketplace-logo"
import { MasterCategoryBadge } from "./master-category-badge"
import { RunStatusBadge } from "./run-status-badge"
import { RunReadinessBadge } from "./run-readiness-badge"
import { useArchiveRun, useUnarchiveRun, useValidateRun } from "../../services/use-runs"

type RunHeaderProps = {
  run: ListingRun
  canValidate: boolean
  onValidated: () => void
  readiness?: RunStatusSummary["readiness"] | undefined
}

export function RunHeader({ run, readiness, canValidate, onValidated }: RunHeaderProps) {
  return (
    <header className="shrink-0 space-y-2">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
        <div className="flex min-w-0 items-center gap-2">
          <MarketplaceLogo
            marketplace={run.marketplace}
            className="size-6 shrink-0 rounded-lg sm:size-7"
          />

          <h1 className="min-w-0 break-words text-lg sm:text-xl font-semibold tracking-tight text-foreground">
            {humanizeMarketplaceId(run.marketplace)}
          </h1>
        </div>

        <div className="order-3 flex basis-full flex-wrap items-center gap-2 sm:order-none sm:basis-auto">
          {run.masterCategory ? (
            <MasterCategoryBadge category={run.masterCategory} />
          ) : (
            <StatusBadge tone="muted">No category</StatusBadge>
          )}

          {run.status !== "open" && <RunStatusBadge status={run.status} />}

          {run.origin === "automatic" ? (
            <StatusBadge tone="brand" title={automaticSourceSummary(run)}>
              Automatic relist
            </StatusBadge>
          ) : null}

          {run.origin === "automatic" ? (
            <StatusBadge tone={run.automaticDirtyAt ? "warning" : "success"}>
              {run.automaticDirtyAt ? "Dirty — rebuild required" : "Current"}
            </StatusBadge>
          ) : null}

          <RunReadinessBadge readiness={readiness} />

          <OffersNeededStatus count={run.offersNeededCount} />

          {run.archivedAt && <StatusBadge tone="muted">Archived</StatusBadge>}
        </div>

        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          <ValidateButton run={run} canValidate={canValidate} onValidated={onValidated} />

          <ArchiveButton run={run} />
        </div>
      </div>

      {run.error && <p className="break-words text-sm text-destructive">{run.error}</p>}
    </header>
  )
}

function plural(count: number, noun: string): string {
  return count === 1 ? noun : `${noun}s`
}

function formatCount(n: number): string {
  return n.toLocaleString("en-US")
}

/** Source-event detail for the Automatic relist badge's hover text; kept off
 *  the meta strip so the header stays one short line. */
function automaticSourceSummary(run: ListingRun): string {
  const events = run.automaticSourceEventCount
  const parts = [`${formatCount(events)} source ${plural(events, "event")}`]
  if (run.automaticLatestEventId !== null) {
    parts.push(`latest #${run.automaticLatestEventId}`)
  }
  if (run.automaticLatestSourceUpdatedAt) {
    parts.push(`source changed ${formatDateTime(run.automaticLatestSourceUpdatedAt)}`)
  }
  return parts.join(" · ")
}

export function ValidateButton({
  run,
  canValidate,
  onValidated,
}: {
  run: Pick<ListingRun, "slug">
  canValidate: boolean
  onValidated: () => void
}) {
  const validate = useValidateRun(run.slug)
  const descriptionId = useId()
  const description = canValidate
    ? "Check feed gaps and classify every product OK / NOK"
    : "Validation requires current product values and a successful status check"
  return (
    <Tooltip content={description}>
      <Button
        variant="secondary"
        size="sm"
        className="px-2.5 sm:px-3.5"
        disabled={!canValidate || validate.isPending}
        aria-describedby={descriptionId}
        aria-label={
          validate.isPending
            ? "Checking gaps and product verdicts"
            : "Check gaps and product verdicts"
        }
        onClick={() => validate.mutate(undefined, { onSuccess: onValidated })}
      >
        <RefreshCw aria-hidden className={cn("size-3.5", validate.isPending && "animate-spin")} />

        {validate.isPending ? "Checking…" : "Check"}
      </Button>

      <span id={descriptionId} className="sr-only">
        {description}
      </span>
    </Tooltip>
  )
}

function ArchiveButton({ run }: { run: ListingRun }) {
  const archive = useArchiveRun()
  const unarchive = useUnarchiveRun()
  const isArchived = run.archivedAt !== null
  const pending = archive.isPending || unarchive.isPending

  const label = isArchived ? "Unarchive run" : "Archive run"
  return (
    <Tooltip content={label}>
      <Button
        variant="ghost"
        size="sm"
        className="w-8 px-0"
        aria-label={label}
        aria-busy={pending}
        disabled={pending}
        onClick={() => (isArchived ? unarchive.mutate(run.slug) : archive.mutate(run.slug))}
      >
        {isArchived ? (
          <ArchiveRestore aria-hidden className="size-4" />
        ) : (
          <Archive aria-hidden className="size-4" />
        )}
      </Button>
    </Tooltip>
  )
}

/** "N offers needed" as of the last offer-snapshot pull — eligible variants
 *  with no marketplace offer yet, what a new-only offer build would push. Hidden
 *  when 0 or null (nothing actionable / never checked), warning-toned because
 *  it's an action to take, not a build blocker like gaps. */
function OffersNeededStatus({ count }: { count: number | null }) {
  if (count === null || count === 0) {
    return null
  }
  return (
    <StatusBadge
      tone="warning"
      title={`${formatCount(count)} variants without offers at the last validation; current preview counts may differ`}
    >
      <Package className="size-3 shrink-0" />

      {`${formatCount(count)} ${plural(count, "offer")} needed (saved)`}
    </StatusBadge>
  )
}
